"use client";

/**
 * The vendor-intent partner page: "who do I hire to deploy Claude here".
 *
 * Deliberately NOT the same job as /partners/anthropic-claude-partner/, which
 * explains what the Claude Partner Network is and what our Certified status means. That
 * page reads as an explainer — which is exactly why it ranks for informational
 * queries and loses the geo-modified vendor ones. This page answers the buyer's
 * question instead: why Claude, why us, and what we have actually shipped.
 *
 * Localised: every visible string goes through t(locale, …), and the catch-all
 * route passes locale in as a prop.
 */

import Link from "next/link";
import { motion, useReducedMotion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import {
  ArrowRight,
  BadgeCheck,
  Boxes,
  GitBranch,
  MapPin,
  Plug,
  Ruler,
  ShieldCheck,
  Siren,
} from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { AnthropicPartnerBadge } from "@/components/ui/anthropic-partner-badge";
import ServiceFaq from "@/components/services/ServiceFaq";
import { StructuredData } from "@/components/common";
import { renderAccent } from "@/lib/render-accent";
import { t } from "@/content/i18n/messages";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { whatsappHref } from "@/lib/whatsapp";
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

/** Why Claude is our default. Each reason is an engineering property we can
 *  point at, not a preference — the page loses its credibility the moment it
 *  reads as loyalty rather than measurement. */
const whyClaude: Array<{ title: string; body: string; icon: LucideIcon }> = [
  {
    title: "The agent protocol came from here",
    body: "Anthropic authored the Model Context Protocol — the standard way an agent reaches your systems. Building agents on the model whose team designed the protocol means fewer adapters between our code and your CRM, ERP or knowledge base, and fewer things to re-plumb when the protocol moves.",
    icon: Plug,
  },
  {
    title: "It holds up over long tool chains",
    body: "A demo agent calls one tool. A production agent calls forty, in sequence, and has to notice when step nine returned something wrong. Claude's tool-use behaviour over long horizons is the single property that decides whether an agent survives contact with real data, and it is why our agents lean on it.",
    icon: GitBranch,
  },
  {
    title: "The enterprise default is the safe one",
    body: "On the API and Team plans, your data is not used to train the models by default. That matters when the first question from a Malaysian or Singaporean compliance team is what happens to customer records — a default we do not have to caveat is worth a great deal in a procurement review.",
    icon: ShieldCheck,
  },
  {
    title: "Long context without a long-context tax",
    body: "Opus carries a one-million-token context window at standard pricing. For document-heavy work — contracts, claims files, a year of correspondence — that removes an entire category of chunking workarounds we would otherwise have to build and maintain.",
    icon: Boxes,
  },
];

const faqs = [
  {
    question: "Why do you choose Claude over OpenAI?",
    answer:
      "For agentic work, three properties decide it: Anthropic authored the Model Context Protocol we integrate through, Claude holds up better across long tool chains where an agent has to notice its own mistakes, and the enterprise default is that your data is not used for training. Those are engineering reasons, not loyalty. We deploy other models — including OpenAI's — where they fit the workload better, and we show you the comparison rather than asking you to take our word for it.",
  },
  {
    question: "When would you NOT recommend Claude?",
    answer:
      "Several cases. Realtime voice and image generation are areas where other models are ahead. If a workload is extremely high-volume and genuinely simple — classifying a million short strings — a cheaper small model often wins on cost per task. And if your organisation has already standardised on another platform with working governance around it, the switching cost usually outweighs the model difference. We say so when that is the answer.",
  },
  {
    question: "What is a Claude Certified Architect?",
    answer:
      "Anthropic's certification for designing and building production systems on Claude — architecture, model selection, evaluation and the operational side, not prompt tips. We have multiple certified architects on the delivery team, and the one who designs your system is the one who signs off on its evaluation.",
  },
  {
    question: "Are you the official Anthropic partner in Malaysia or Singapore?",
    answer:
      "No, and neither is anyone else — there is no exclusive arrangement in either market. Anchor Sprint is Certified in the Claude Partner Network. Several companies are members. What separates us is not the badge: it is a Malaysian-owned engineering team with certified architects, a published record of the agents we have built, and the operational depth to run them after launch.",
  },
  {
    question: "Do you work in both Malaysia and Singapore?",
    answer:
      "Yes. Anchor Sprint Sdn Bhd delivers from Setia Alam, Shah Alam, working on site across the Klang Valley, and Anchor Sprint Pte. Ltd. (UEN 202632732Z) delivers from Suntec Tower One in Singapore. Malaysian engagements are scoped in MYR against PDPA; Singaporean ones in SGD against PDPC, with MAS TRM artefacts where your regulator expects them.",
  },
  {
    question: "What if we want to move off Claude later?",
    answer:
      "The agent, the evaluations and the integrations are yours. We build the model as a swappable component behind our own interface rather than scattering vendor-specific calls through your codebase, precisely so that a model decision made in 2026 is not a cage in 2028. Model-agnostic is an architecture choice, not a slogan.",
  },
];

/** The reliability record behind the model choice. These are engagements from
 *  before the AI work — the reason we design agents for the 2am failure. */
const engineeringProof = [
  { figure: "300+", label: "production incidents handled" },
  { figure: "30+", label: "resilience assessments run" },
  { figure: "50+", label: "load-test audits completed" },
];

export default function ClaudePartnerMalaysiaSingapore({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const reduce = useReducedMotion();
  const localisedFaqs = faqs.map(({ question, answer }) => ({
    question: t(locale, question),
    answer: t(locale, answer),
  }));
  const inView = {
    initial: reduce ? false : { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.55, ease: [0.21, 0.6, 0.35, 1] as const },
  };

  return (
    <div className="bg-background">
      <StructuredData type="faq" faqs={localisedFaqs} />

      {/* ── Hero: the thesis, not a summary ── */}
      <section className="border-b border-border bg-neutral-surface">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="grid items-center gap-12 lg:grid-cols-[1.05fr_1fr] lg:gap-16">
            <div>
              <p className="mb-5 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Why Claude · Malaysia & Singapore")}
              </p>
              <h1 className="font-display text-4xl font-bold leading-[1.1] tracking-tight text-neutral-emphasis md:text-5xl">
                {renderAccent(
                  t(
                    locale,
                    "We build on [[Claude]] — and we can show you the measurements that decided it.",
                  ),
                )}
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-relaxed text-neutral-muted md:text-xl">
                {t(
                  locale,
                  "Anchor Sprint is a Malaysian-owned engineering company and Certified in the Claude Partner Network, with Claude Certified Architects on the delivery team. We deploy production AI agents for businesses in Malaysia and Singapore. Claude is our default — not because we are fond of it, but because of four properties that decide whether an agent survives real data.",
                )}
                {/* This page explains the model choice; the partner claim lives on the
                    owner page, so link to it instead of restating it (2026-10-08). */}
                {" "}
                {t(locale, "What our Certified status covers is set out on our page as a ")}
                <Link
                  href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)}
                  className="font-medium text-primary underline underline-offset-4"
                >
                  {t(locale, "Claude partner in Malaysia")}
                </Link>
                {t(locale, " — including what we do not claim.")}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <AnthropicPartnerBadge variant="hero" />
                <a
                  href={whatsappHref("Hi Anchor Sprint, I'd like to talk about Claude for my company (from your Why we build on Claude page).")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="claude_partner_my_sg_hero"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {t(locale, "WhatsApp us about Claude")}
                </a>
                <Link
                  href={getLocalizedPathname("/contact/", locale)}
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-border px-7 py-3 text-sm font-semibold text-neutral-emphasis transition hover:border-primary hover:text-primary"
                >
                  {t(locale, "Scope a deployment")}
                </Link>
              </div>
            </div>
            <motion.div
              // Scale only: opacity stays 1 so the hero image paints before
              // hydration (LCP).
              initial={reduce ? false : { scale: 0.97 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.8, ease: [0.21, 0.6, 0.35, 1] }}
              className="overflow-hidden rounded-2xl border border-border"
            >
              <img
                src="/images/partners/claude-partner-my-sg-hero.jpg"
                alt={t(
                  locale,
                  "A brass caliper measuring one lit sphere selected from three unlit ones — choosing an AI model by measurement",
                )}
                width={1400}
                height={933}
                className="h-full w-full object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Why Claude ── */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-14 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "The model decision")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Why we build on Claude rather than OpenAI")}
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
              {t(
                locale,
                "Every AI agency says it is model-agnostic and then quietly uses one model for everything. We will be specific instead. Here is what made Claude our default for agentic work, and further down, the cases where we tell clients to use something else.",
              )}
            </p>
          </motion.div>
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {whyClaude.map(({ title, body, icon: Icon }, i) => (
              <motion.div
                key={title}
                {...inView}
                transition={{
                  duration: 0.5,
                  delay: i * 0.08,
                  ease: [0.21, 0.6, 0.35, 1] as const,
                }}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-7"
              >
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10">
                  <Icon className="h-5 w-5 text-primary" aria-hidden />
                </div>
                <h3 className="mb-3 font-display text-lg font-semibold text-neutral-emphasis">
                  {t(locale, title)}
                </h3>
                <p className="text-sm leading-relaxed text-neutral-muted">
                  {t(locale, body)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── The honest half: where we don't recommend Claude ── */}
      <section className="border-y border-border bg-neutral-surface py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "The other half of an honest answer")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Where we tell you to use something else")}
            </h2>
            <div className="mt-6 space-y-4 text-lg leading-relaxed text-neutral-muted">
              <p>
                {t(
                  locale,
                  "Realtime voice and image generation are areas where other models are ahead, and we say so. For very high-volume, genuinely simple work — classifying a million short strings — a cheaper small model usually wins on cost per task, whoever makes it. And if your organisation has already standardised on another platform with governance that works, the switching cost normally outweighs the model difference.",
                )}
              </p>
              <p>
                {t(
                  locale,
                  "We also build the model as a swappable component behind our own interface, rather than scattering vendor-specific calls through your codebase. A model decision made in 2026 should not be a cage in 2028.",
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Homegrown engineering ── */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div {...inView}>
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
                {t(locale, "Who builds it")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
                {t(locale, "A home-grown engineering team, not a reseller desk")}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-neutral-muted">
                {t(
                  locale,
                  "Most AI agencies started with prompts and are learning production the hard way. We travelled in the other direction. Our lead engineer spent years on the systems that sit behind large enterprises — which is why we design an agent for its 2am failure first.",
                )}
              </p>
              <dl className="mt-9 grid grid-cols-3 gap-4">
                {engineeringProof.map(({ figure, label }) => (
                  <div key={label} className="rounded-2xl border border-border bg-card p-5">
                    <dt className="font-display text-2xl font-bold text-neutral-emphasis">
                      {figure}
                    </dt>
                    <dd className="mt-1 text-xs leading-snug text-neutral-muted">
                      {t(locale, label)}
                    </dd>
                  </div>
                ))}
              </dl>
            </motion.div>

            <motion.div {...inView} className="space-y-5">
              {[
                {
                  icon: BadgeCheck,
                  title: "Claude Certified Architects on the delivery team",
                  body: "Anthropic's certification for designing and building production systems on Claude — architecture, model selection, evaluation and operations. The architect who designs your system signs off on its evaluation.",
                },
                {
                  icon: MapPin,
                  title: "Malaysian-owned, delivered from both markets",
                  body: "Anchor Sprint Sdn Bhd works out of Setia Alam, Shah Alam and on site across the Klang Valley. Anchor Sprint Pte. Ltd. (UEN 202632732Z) delivers from Suntec Tower One in Singapore. MDEC Malaysia Digital status; HRD Corp approved training provider.",
                },
                {
                  icon: Siren,
                  title: "We stay after launch",
                  body: "Agents drift. Vendors change APIs. Your data changes shape. We run monitoring, evaluations and the on-call path, because the deployment that matters is the one still working in month nine.",
                },
                {
                  icon: Ruler,
                  title: "Every agent graded before go-live",
                  body: "A published evaluation scorecard per build — what it was measured on, what it scored, and where a human stays in the loop. You approve the numbers before it touches a customer.",
                },
              ].map(({ icon: Icon, title, body }) => (
                <div key={title} className="flex gap-4 rounded-2xl border border-border bg-card p-6">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
                    <Icon className="h-5 w-5 text-primary" aria-hidden />
                  </div>
                  <div>
                    <h3 className="font-display text-base font-semibold text-neutral-emphasis">
                      {t(locale, title)}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-neutral-muted">
                      {t(locale, body)}
                    </p>
                  </div>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Editorial links to the aideploy delivery sites. The footer already
              links them sitewide, but a repeated footer link carries roughly one
              weak signal in total — which is why every aideploy inner page still
              reports "Discovered - currently not indexed" with no crawl recorded. */}
          <motion.p
            {...inView}
            className="mx-auto mt-14 max-w-3xl text-center leading-relaxed text-neutral-muted"
          >
            {t(
              locale,
              "Deployment work runs on our dedicated delivery sites — what a build involves, what it costs, and the worked examples behind it: ",
            )}
            <a
              href="https://aideploy.my/services/"
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "AI Deploy Malaysia")}
            </a>
            {t(locale, " and ")}
            <a
              href="https://aideploy.sg/services/"
              className="font-medium text-primary underline underline-offset-4"
            >
              {t(locale, "AI Deploy Singapore")}
            </a>
            {t(locale, ".")}
          </motion.p>
        </div>
      </section>

      {/* ── Proof strip ── */}
      <section className="border-t border-border bg-neutral-surface py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-10 max-w-3xl">
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Check us before you call us")}
            </h2>
            <p className="mt-4 text-lg text-neutral-muted">
              {t(
                locale,
                "Everything below is published. Read it, compare it against what other agencies in Malaysia and Singapore publish, and arrive at the first call already knowing how we work.",
              )}
            </p>
          </motion.div>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              {
                href: "/services/training-education/",
                title: "HRD Corp claimable AI training",
                body: "AI Fluency courses taught on site in Malaysia, claimable under SBL Khas against the levy you already pay. We prepare the e-TRiS grant application and file the claim.",
              },
              {
                href: "/solutions/agent-use-cases/",
                title: "The agent use-case directory",
                body: "Worked builds — order processing, invoice matching, credit evaluation and more — each with the workflow, the architecture and the evaluation scorecard.",
              },
              {
                href: "/tools/claude-cost-calculator/",
                title: "The Claude cost calculator",
                body: "Your team's real monthly and three-year cost in Ringgit or Singapore dollars, with an Excel breakdown for management. No email gate to see the number.",
              },
              {
                href: "/partners/anthropic-claude-partner/",
                title: "What our Certified status means",
                body: "The credential itself — what the Claude Partner Network is, what our Certified status gives your project, and what we deliberately do not claim.",
              },
            ].map(({ href, title, body }) => (
              <motion.div key={href} {...inView}>
                <Link
                  href={getLocalizedPathname(href, locale)}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-7 transition-colors hover:border-primary"
                >
                  <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                    {t(locale, title)}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-neutral-muted">
                    {t(locale, body)}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-all group-hover:gap-2.5">
                    {t(locale, "Open")} <ArrowRight className="h-4 w-4" aria-hidden />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background px-4 py-12 sm:px-6 lg:px-8">
        <GovernanceCallout locale={locale} variant="rollout" placement="claude_partner_my_sg" />
      </section>

      {/* ── FAQ ── */}
      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="mb-12 max-w-3xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
              {t(locale, "FAQ")}
            </p>
            <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-4xl">
              {t(locale, "Questions buyers actually ask us")}
            </h2>
          </motion.div>
          <ServiceFaq items={localisedFaqs} />
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-background pb-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...inView} className="rounded-3xl bg-hero-background p-10 sm:p-14">
            <div className="max-w-3xl">
              <p className="mb-4 text-sm font-semibold uppercase tracking-[0.3em] text-primary-contrast/60">
                {t(locale, "Start a conversation")}
              </p>
              <h2 className="font-display text-3xl font-bold leading-tight text-primary-contrast md:text-4xl">
                {t(
                  locale,
                  "Tell us the workflow. We will tell you whether an agent is the right answer.",
                )}
              </h2>
              <p className="mt-4 text-lg text-primary-contrast/70">
                {t(
                  locale,
                  "A scoping call, not a pitch. Describe the process and the data it touches, and you will get an honest read on whether Claude — or anything else — is worth deploying against it, with a cost estimate in MYR or SGD.",
                )}
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={whatsappHref("Hi Anchor Sprint, I'd like to talk about Claude for my company (from your Why we build on Claude page).")}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-track-event="contact_whatsapp_click"
                  data-track-location="claude_partner_my_sg_cta"
                  data-track-phone="601159246128"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-primary-emphasis"
                >
                  <FaWhatsapp className="h-4 w-4" aria-hidden />
                  {t(locale, "WhatsApp us")}
                </a>
                <Link
                  href={getLocalizedPathname("/contact/", locale)}
                  className="group inline-flex items-center justify-center gap-2 rounded-full border border-primary-contrast/20 px-8 py-3 text-sm font-semibold text-primary-contrast transition hover:border-primary-contrast/40 hover:bg-primary-contrast/10"
                >
                  {t(locale, "Book a scoping call")}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-1"
                    aria-hidden
                  />
                </Link>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-primary-contrast/50">
                {t(
                  locale,
                  "Anchor Sprint is Certified in the Claude Partner Network. We are not an official, exclusive or endorsed representative of Anthropic in either market, and no such arrangement exists for anyone. Claude and Anthropic are trademarks of Anthropic, PBC.",
                )}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
